import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import Typography from '@material-ui/core/Typography';
import Grid from '@material-ui/core/Grid';
import Box from '@material-ui/core/Box';
import { getRecurringPayment } from 'utils';

import { useStyles, listTextStyles, customCardStyles, collapseCardStyles } from './Details.styled';
import { Card, CollapseCard } from 'components/UI';
import { SubscriptionsActions } from 'state/modules/subscriptions';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';
import { hideActions } from 'constants/subscriptions';
import moment from 'moment';
import Actions from './Actions';
import ItemStatus from 'components/ItemStatus';
import Can from 'components/Can';
import {
  List,
  ListItem,
  IconButton,
  ListItemSecondaryAction,
  ListItemText,
} from '@material-ui/core';
import i18n from 'i18n-js';

const SubscriptionDetails = ({ id }) => {
  const classes = useStyles();
  const dispatch = useDispatch();

  const listClasses = listTextStyles();
  const collapseCardClasses = collapseCardStyles();
  const cardClasses = customCardStyles();
  const { subscription } = useSelector((state) => state.subscriptions);
  const { localeFormat } = useSelector((state) => state.organizations);

  useEffect(() => {
    if (id) {
      dispatch(SubscriptionsActions.fetchSubscriptionById(id));
    }
  }, [id]); // eslint-disable-line

  const parsePricing = (pricing = []) => {
    return pricing.map((item) => {
      return {
        label: `${item?.description || ''} ${
          item?.invoice_item_type?.units?.per_unit
            ? '(' + item?.invoice_item_type?.units?.per_unit?.replace(/\//, 'per').trim() + ')'
            : ''
        }`,
        value: item?.unit_price ? `$${(item?.unit_price / 100).toFixed(2)}` : 0,
      };
    });
  };

  const getDatetime = (datetime, tz = moment.tz.guess()) => {
    if (datetime) {
      return (
        <span>
          <p>{moment(datetime).tz(tz).format(`${localeFormat} hh:mm A`)}</p>
          <p className={classes.timezone}>{moment().tz(tz).format('UTC Z')}</p>
        </span>
      );
    }
    return '';
  };

  const getSubscriptionData = (subscription = {}) => {
    const startAddress = subscription?.exchanges?.START?.exchange_address;
    const endAddress = subscription?.exchanges?.END?.exchange_address;

    const subscriptionInfo = [
      {
        title: i18n.t('subscriptions.details'),
        items: [
          {
            label: `${i18n.t('subscriptions.period_length')}:`,
            value: subscription?.periods_reserved || '-',
          },
          {
            label: `${i18n.t('subscriptions.periodic_payment')}:`,
            value: (
              <span className={classes.active}>
                {getRecurringPayment(subscription?.pricing) || '-'}
              </span>
            ),
          },
          {
            label: `${i18n.t('subscriptions.term_balance')}:`,
            value: subscription?.period_type
              ? i18n.t(`subscriptions.period_type.${subscription?.period_type}`, {
                  defaultValue: subscription?.period_type || '',
                })
              : '-',
          },
          {
            label: `${i18n.t('subscriptions.vin')}:`,
            value: subscription?.vehicle?.vin || '-',
          },
          {
            label: `${i18n.t('subscriptions.start_address')}:`,
            value: `${startAddress?.street_address_line_1 || ''} 
                    ${startAddress?.street_address_line_2 || ''} 
                    ${startAddress?.state || ''} ${startAddress?.zip_code || ''}`,
          },
          {
            label: `${i18n.t('subscriptions.end_address')}:`,
            value: `${endAddress?.street_address_line_1 || ''} 
                    ${endAddress?.street_address_line_2 || ''} 
                    ${endAddress?.state || ''} ${endAddress?.zip_code || ''}`,
          },
          {
            label: `${i18n.t('subscriptions.schedule_delivery')}:`,
            value: getDatetime(subscription?.exchanges?.START?.reserved_time) || '-',
          },
          {
            label: `${i18n.t('subscriptions.mark_delivered')}:`,
            value: getDatetime(subscription?.exchanges?.START?.used_time) || '-',
          },
          {
            label: `${i18n.t('subscriptions.schedule_return')}:`,
            value: getDatetime(subscription?.exchanges?.END?.reserved_time) || '-',
          },
          {
            label: `${i18n.t('subscriptions.mark_returned')}:`,
            value: getDatetime(subscription?.exchanges?.END?.used_time) || '-',
          },
        ],
      },
      {
        title: i18n.t('subscriptions.pricing'),
        items: parsePricing(subscription?.pricing),
      },
      {
        title: i18n.t('subscriptions.documents'),
        hide: !subscription?.contract_executed?.url_path,
        items: [
          {
            label: i18n.t('subscriptions.contract'),
            value: (
              <ListItemSecondaryAction>
                <IconButton
                  edge="end"
                  target="_blank"
                  aria-label="right"
                  href={subscription?.contract_executed?.url_path}
                  classes={{
                    root: classes.documentIconRoot,
                    label: classes.documentIconLabel,
                  }}
                >
                  <ArrowForwardIcon />
                </IconButton>
              </ListItemSecondaryAction>
            ),
          },
        ],
      },
    ];

    return subscriptionInfo;
  };

  const showActions = (status) => {
    return hideActions.includes(status) ? false : true;
  };

  return (
    <Card
      title={i18n.t('subscriptions.information')}
      headerActionType="vertical"
      contentRootClasses={cardClasses}
    >
      <>
        <div className={classes.cardContentPadding}>
          <Grid container direction="row" alignItems="center" spacing={1}>
            <Grid item xs={4}>
              <Typography variant="h3">
                <Box className={classes.name}>ID# {id}</Box>
              </Typography>
            </Grid>
            {subscription?.status && (
              <Grid item xs={8} className={`${classes.textRight}`}>
                <ItemStatus
                  i18nKey="subscriptions.statuses"
                  status={subscription?.status}
                  upperCase={true}
                />
              </Grid>
            )}
          </Grid>

          <Can I="update" on="subscription">
            {showActions(subscription?.status) && (
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Actions id={id} status={subscription?.status} listStyles={listClasses} />
                </Grid>
              </Grid>
            )}
          </Can>

          <div>
            {getSubscriptionData(subscription).map((val, index) => (
              <Box key={index}>
                {!val?.hide && (
                  <CollapseCard key={index} title={val.title} customClasses={collapseCardClasses}>
                    <List className={classes.userList}>
                      {val.items.map((val, index) => (
                        <ListItem key={index} className={classes.subscriptionListItem}>
                          <ListItemText
                            classes={listClasses}
                            primary={val.label}
                            secondary={
                              <Box
                                display="flex"
                                component="span"
                                flexDirection="column"
                                className={classes.textRight}
                              >
                                <Typography
                                  variant="body2"
                                  component="span"
                                  color="textSecondary"
                                  className={classes?.[val.classes]}
                                >
                                  {val.value}
                                </Typography>
                                {val.hasOwnProperty('confirmed') &&
                                  val.confirmed !== 'Unconfirmed' && (
                                    <Typography
                                      variant="body2"
                                      component="span"
                                      className={classes.confirmed}
                                    >
                                      {i18n.t('users.confirmed')}:{' '}
                                      {moment(val.confirmed).format(`${localeFormat} hh:mm a`)}
                                    </Typography>
                                  )}
                                {val.hasOwnProperty('confirmed') &&
                                  val.confirmed === 'Unconfirmed' && (
                                    <Typography
                                      variant="body2"
                                      component="span"
                                      className={classes.unconfirmed}
                                    >
                                      {val.confirmed}
                                    </Typography>
                                  )}
                              </Box>
                            }
                          />
                        </ListItem>
                      ))}
                    </List>
                  </CollapseCard>
                )}
              </Box>
            ))}
          </div>
        </div>
      </>
    </Card>
  );
};

export default SubscriptionDetails;
